import { useWorkspaceDailySource } from '../WorkspaceDailySource.jsx'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useCandleStore } from '../../../hooks/useCandleStore.js'
import { toCandles } from '../../../lib/candleStore.js'
import { subscribeIbkrChartStream } from '../../../lib/marketData/ibkrChartAdapter.js'
import { reconcileRuntimeCandle } from '../../../lib/chart/formingBar.js'

const EMPTY = []

// Reuse the workspace's normalized history, scrollback, and authoritative
// runtime stream. No indicator, earnings, or independent timeframe state.
export function useRsCandles({ symbol, timeframe, marketSession, chartProps, role }) {
  const timezone = chartProps.timezone || 'America/Chicago'
  const identity = JSON.stringify([symbol, timeframe, marketSession, timezone])
  const [connection, setConnection] = useState(null)
  const [live, setLive] = useState({ identity: null, bars: [] })
  const current = useRef({ identity, bars: [] })
  const correct = useCallback((bar, completed = false) => {
    if (current.current.identity !== identity || !Number.isFinite(bar?.close) || bar.close <= 0) return
    const result = reconcileRuntimeCandle(current.current.bars, bar, { completed })
    if (result.accepted) { current.current.bars = result.bars; setLive({ identity, bars: result.bars }) }
  }, [identity])
  const shared = useWorkspaceDailySource({ symbol, timeframe, marketSession, timezone, candleAuthority: chartProps.candleAuthority || 'ibkr-runtime', enabled: role === 'asset' && chartProps.mode !== 'static' && (chartProps.assetType || 'equity') === 'equity' })
  const independent = useCandleStore({ symbol, timeframe, marketSession, timezone,
    candleAuthority: chartProps.candleAuthority || 'ibkr-runtime',
    assetType: role === 'asset' ? chartProps.assetType || 'equity' : 'equity',
    optContract: role === 'asset' ? chartProps.optContract : null,
    enabled: !shared && chartProps.mode !== 'static' && Boolean(symbol), onFormingBarUpdate: correct })
  const source = shared || independent
  const isCurrent = source.status === 'ready' && (!source.requestKey || source.requestKey === source.candlesKey)
  const snapshot = chartProps.mode === 'static' ? chartProps.bars || EMPTY : isCurrent ? source.candles : null
  useEffect(() => {
    const bars = snapshot || EMPTY
    current.current = { identity, bars }
    setConnection(null)
    setLive({ identity, bars })
  }, [identity, snapshot])
  useEffect(() => {
    if (!isCurrent || !source.streamUrl || chartProps.mode === 'static') return undefined
    let disposed = false
    const accept = (raw, completed) => { if (!disposed) { const bar = toCandles([raw], timezone)[0]; if (bar) correct(bar, completed) } }
    const stop = (shared?.subscribe || subscribeIbkrChartStream)({ streamUrl: source.streamUrl, timeframe, reconcile: source.reconcileStream,
      onFormingCandle: raw => accept(raw, false), onCompletedCandle: raw => accept(raw, true), onState: ({ state }) => { if (!disposed) setConnection(state) }, onError: () => {} })
    return () => { disposed = true; stop() }
  }, [shared?.subscribe, identity, isCurrent, source.streamUrl, source.reconcileStream, correct, timeframe, timezone, chartProps.mode])
  return { ...source, identity, connection, bars: live.identity === identity && snapshot ? live.bars : EMPTY,
    status: chartProps.mode === 'static' ? 'ready' : isCurrent ? 'ready' : source.status === 'ready' ? 'loading' : source.status }
}
